import { useState, useEffect, useCallback } from "react";
import { ClientOverviewRepositoryImpl } from "../../data/repositories/ClientOverviewRepositoryImpl";
import { GetTwelveMonthRevenueInsightsUseCase } from "../../domain/use-cases/GetTwelveMonthRevenueInsightsUseCase";
import type { TwelveMonthRevenueInsightItem } from "../../domain/entities/TwelveMonthRevenueInsightsEntity";

const repository = new ClientOverviewRepositoryImpl();
const useCase = new GetTwelveMonthRevenueInsightsUseCase(repository);

export interface TwelveMonthRevenueInsightsData {
  insights: TwelveMonthRevenueInsightItem[];
  rangeLabel: string;
  monthCount: number;
  noInsights: boolean;
  message: string | null;
  loading: boolean;
  error: string | null;
}

export interface UseTwelveMonthRevenueInsightsOptions {
  /** When false, the insights API is not called. Set true only after twelve-month trend has loaded. */
  enabled?: boolean;
}

export function useTwelveMonthRevenueInsights(
  options?: UseTwelveMonthRevenueInsightsOptions,
): TwelveMonthRevenueInsightsData {
  const enabled = options?.enabled ?? true;

  const [insights, setInsights] = useState<TwelveMonthRevenueInsightItem[]>([]);
  const [rangeLabel, setRangeLabel] = useState("");
  const [monthCount, setMonthCount] = useState(0);
  const [noInsights, setNoInsights] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInsights = useCallback(async () => {
    if (!enabled) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const entity = await useCase.execute();
      setInsights(entity.insights);
      setRangeLabel(entity.rangeLabel);
      setMonthCount(entity.monthCount);
      setNoInsights(entity.noInsights);
      setMessage(entity.message);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load revenue trend insights",
      );
      setInsights([]);
      setMonthCount(0);
      setNoInsights(true);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchInsights();
  }, [enabled, fetchInsights]);

  return {
    insights,
    rangeLabel,
    monthCount,
    noInsights,
    message,
    loading,
    error,
  };
}
